import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime'

import { STATSIG_EXPERIMENTS, STATSIG_EXPERIMENTS_CONFIG } from '@src/config/statsig-experiments.config'
import { QuinceRouter } from '@src/modules/navigation'
import { ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import { getAdditionalProductTagsForCard } from '@src/quince-site-common/src/utils/festivalOffersUtility'
import { GenderName, getCurrentPageGenderContext } from '@src/quince-site-common/src/utils/getCurrentPageGenderContext'
import { getPathName } from '@src/quince-site-common/src/utils/getPathName'
import { getPathParams } from '@src/quince-site-common/src/utils/getPathParam'
import { getURLStringFromInputString } from '@src/quince-site-common/src/utils/getURLStringFromInputString'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { isEmpty } from '@src/utils/functions/utils'

import { CardVariant, IProductCard, ProductCardTag } from './product-card.types'
export const QUERY_PARAM_TRACKER = 'tracker'
export const QUERY_PARAM_SEARCH_QUERY = 'searchQuery'
export const QUERY_PARAM_PRODUCT_POSITION = 'productPosition'
export const QUERY_PARAM_VALUE_SEARCH_PAGE_SEARCH_SECTION_SEARCH_RESULTS = 'searchPage__search_section__search_results'

export function getInitialSelectedVariant({
  selectedCardVariantId = undefined,
  cardVariants = [],
}: Pick<IProductCard['product'], 'cardVariants'> & { selectedCardVariantId?: number }) {
  if (!selectedCardVariantId) return cardVariants[0]
  const cardVariant = cardVariants.find(cardVariant => cardVariant.variantId === selectedCardVariantId)
  return cardVariant ?? cardVariants[0]
}

export function moveSelectedVariantToFront(cardVariants: CardVariant[] = [], selectedColor?: string) {
  const updatedCardVariants = [...cardVariants]
  const selectedVariantIndex = updatedCardVariants.findIndex(
    cardVariant => cardVariant.displayConfig.value === selectedColor,
  )
  const deletedElement = updatedCardVariants.splice(selectedVariantIndex, 1)[0]
  if (deletedElement) {
    updatedCardVariants.unshift(deletedElement)
    return updatedCardVariants
  }
  return cardVariants
}

export function getUpdatedProductCardForSelectedColor(productCard: IProductCard, selectedColor?: string): IProductCard {
  const updatedCardVariants = moveSelectedVariantToFront(productCard.product.cardVariants, selectedColor)
  return { ...productCard, product: { ...productCard.product, cardVariants: updatedCardVariants } }
}

export function getSelectedVariantForSelectedColor({
  cardVariants = [],
  selectedColor,
}: {
  cardVariants: CardVariant[]
  selectedColor?: string
}) {
  if (!selectedColor) {
    return undefined
  }

  return cardVariants.find(cardVariant => cardVariant.displayConfig.value === selectedColor)
}

export const navigateToPDP = ({
  slug,
  tracker,
  selectedOption,
  additionalAttributes,
  router,
}: {
  slug: string
  tracker: string | undefined
  selectedOption?: string
  additionalAttributes?: Record<string, string>
  router: AppRouterInstance | QuinceRouter
}) => {
  const productLink = getProductLink(slug, selectedOption, tracker, additionalAttributes)
  router.push(productLink)
}

export const getProductLink = (
  slug: string,
  selectedOption?: string,
  tracker?: string,
  additionalAttributes?: Record<string, string>,
  searchQuery?: string,
  position?: number,
) => {
  const params: Record<string, string> = {}
  const color = selectedOption
  if (color) {
    params.color = getURLStringFromInputString(color)
  }
  if (tracker) {
    params[QUERY_PARAM_TRACKER] = tracker
  }
  if (searchQuery) {
    params[QUERY_PARAM_SEARCH_QUERY] = searchQuery
  }
  if (position) {
    params[QUERY_PARAM_PRODUCT_POSITION] = position.toString()
  }

  if (additionalAttributes && !isEmpty(additionalAttributes)) {
    Object.entries(additionalAttributes).forEach(([value, key]) => (params[key] = value.toLowerCase()))
  }

  let currentPageSlug = ''
  if (isBrowser()) {
    currentPageSlug = window.location.pathname
  }
  return getPathName(slug) + getPathParams(params, currentPageSlug)
}

export const getProductLinkWithSelectedColor = (slug: string, selectedCardVariant?: CardVariant) => {
  const params: Record<string, string> = {}

  const selectedColor = selectedCardVariant?.displayConfig.value
  if (selectedColor) {
    params.color = getURLStringFromInputString(selectedColor)
  }

  return getPathName(slug) + getPathParams(params, '')
}

export function getImagesForOptions(selectedVariant?: CardVariant, gender?: GenderName | undefined) {
  const productMedia = selectedVariant?.images.filter(image => {
    if (
      (image.description as GenderName) === GenderName.MALE ||
      (image.description as GenderName) === GenderName.FEMALE
    ) {
      let currentPageGenderContext: GenderName | undefined
      if (gender) {
        currentPageGenderContext = gender
      } else if (isBrowser()) {
        const pathname = window.location.pathname
        const search = window.location.search
        currentPageGenderContext = getCurrentPageGenderContext(pathname, search)
      }

      if (currentPageGenderContext && currentPageGenderContext !== (image.description as GenderName)) {
        return false
      }
    }
    return true
  })

  return productMedia ?? []
}

export function getSelectedOptionsForProductCard(selectedCardVariant?: CardVariant): ProductVariantOption[] {
  const selectedOptions: ProductVariantOption[] = []

  if (selectedCardVariant) {
    selectedOptions.push({
      name: selectedCardVariant.displayConfig.name,
      value: selectedCardVariant.displayConfig.value,
    })
  }
  return selectedOptions
}

function removeDuplicateTags(tags: ProductCardTag[]) {
  const tagsSet = new Set()
  return tags.filter(tag => {
    if (tagsSet.has(tag.value)) {
      return false
    }
    tagsSet.add(tag.value)
    return true
  })
}

export function getVariantTags(
  selectedCardVariant?: CardVariant,
  isCreditCardControlBucket?: boolean,
  refreshedTagsForSelectedVariant?: ProductCardTag[],
  urgencyTagsBucket?: string | null,
) {
  let allTags = refreshedTagsForSelectedVariant ?? selectedCardVariant?.tags ?? []

  const isSoldOut = allTags.find(applicableVariantTag => applicableVariantTag.value === 'Sold Out')
  const additionalProductTags = getAdditionalProductTagsForCard(selectedCardVariant)
  
  if (!isSoldOut && additionalProductTags.length > 0) {
    allTags = additionalProductTags
  }

  let isPreorder = false

  const showDefaultTags =
    urgencyTagsBucket !== STATSIG_EXPERIMENTS_CONFIG[STATSIG_EXPERIMENTS.URGENCY_TAGS].buckets?.groupB

  const applicableTags = removeDuplicateTags(allTags)
    .filter(item => {
      switch (item.value) {
        case 'Pre Order':
          isPreorder = true
          return false
        case 'GET IT BY 02/14':
          return false
        case 'Today Only':
          return false
        case 'Best seller':
          return showDefaultTags
        case 'Low Stock':
          return showDefaultTags
        default:
          return true
      }
    })
    .filter(
      item => !(isCreditCardControlBucket && item.value.includes('Earn $') && selectedCardVariant?.vendorType == '3P'),
    )

  return {
    applicableTags,
    isPreorder,
  }
}

export const getQuincePriceForProduct = (cardVariants: CardVariant[]) =>
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  Math.min(...cardVariants.map(variant => variant?.price?.salePrice).filter(val => !!val))

export const hasDifferentPrices = (cardVariants: CardVariant[]) => {
  const somePrice = cardVariants[0]?.price.salePrice
  for (let index = 1; index < cardVariants.length; index++) {
    const variant = cardVariants[index]
    if (somePrice !== variant?.price.salePrice) {
      return true
    }
  }
  return false
}

export const getAllVariantIds = (cardVariants: CardVariant[] = []): string[] => {
  const variantIdsSet = new Set<string>()

  cardVariants.forEach(variant => {
    if (Array.isArray(variant.variantIds)) {
      variant.variantIds.forEach(id => {
        if (id) {
          variantIdsSet.add(id.toString())
        }
      })
    }
  })

  return Array.from(variantIdsSet)
}
